/**
 * ProductCatalog.jsx – Zentrale Produktseite "Produkte"
 * Route: /admin/produkte
 * Phase 3 Implementation
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Search, Package, CheckCircle2, Plus, ChevronDown, ChevronUp,
  Loader2, RefreshCw, AlertCircle, Tag, X, Filter, DatabaseZap,
  Pencil, Trash2, ShieldAlert,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCT_GROUPS = [
  'Frische','Backshop','Getränke_Alkoholfrei','Getränke_Alkoholisch',
  'Süßwaren','Snacks_Salzige','Zeitschriften',
  'Pflegeprodukte','Fahrzeugbedarf','Sonstiges',
];

const MODULES = [
  { value: 'allgemein', label: 'Allgemein' },
  { value: 'mhd', label: 'MHD-Kontrolle' },
  { value: 'backshop', label: 'Backshop' },
  { value: 'bestellungen', label: 'Bestellungen' },
];

export default function ProductCatalog() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperadmin = user?.role === 'superadmin';

  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [locations, setLocations] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState('');

  // Filter-States
  const [search, setSearch] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterSupplier, setFilterSupplier] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // New product dialog
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ product_name: '', gtin: '', brand: '', product_group: '', packaging_type: '', supplier_name: '', supplier_article_number: '' });
  const [submitting, setSubmitting] = useState(false);

  // Correction request dialog
  const [correctionProduct, setCorrectionProduct] = useState(null);
  const [correction, setCorrection] = useState({ field_in_question: '', current_value: '', proposed_value: '', reason: '' });

  // Phase-4-Migration Dialog
  const [migratePreview, setMigratePreview] = useState(null);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  // Tabak-Cleanup Dialog
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleaningUp, setCleaningUp] = useState(false);

  // Superadmin: Produkt bearbeiten
  const [editProduct, setEditProduct] = useState(null);   // Produkt das gerade bearbeitet wird
  const [editData, setEditData] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const LIMIT = 50;

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(LIMIT),
        ...(search && { q: search }),
        ...(filterGroup && { product_group: filterGroup }),
        ...(filterBrand && { brand: filterBrand }),
        ...(filterSupplier && { supplier_name: filterSupplier }),
      });
      const res = await axios.get(`${API}/api/products?${params}`, { headers });
      setProducts(res.data.products || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      toast.error('Fehler beim Laden des Produktkatalogs');
    } finally {
      setLoading(false);
    }
  }, [page, search, filterGroup, filterBrand, filterSupplier]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    axios.get(`${API}/api/admin/locations`, { headers }).then(r => {
      const locs = r.data || [];
      setLocations(locs);
      if (locs.length > 0) setSelectedLocation(locs[0].id);
    }).catch(() => {});
  }, []);

  const handleActivate = async (product) => {
    if (!selectedLocation) { toast.error('Bitte zuerst einen Standort auswählen'); return; }
    try {
      await axios.post(`${API}/api/products/${product.id}/activate`,
        { location_id: selectedLocation, module: 'allgemein' }, { headers });
      toast.success(`"${product.product_name}" für Standort aktiviert`);
      fetchProducts();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Fehler beim Aktivieren';
      toast.error(typeof msg === 'object' ? msg.message : msg);
    }
  };

  const handleDeactivate = async (product) => {
    if (!selectedLocation) return;
    try {
      await axios.post(`${API}/api/products/${product.id}/deactivate`,
        { location_id: selectedLocation, module: 'allgemein' }, { headers });
      toast.success(`"${product.product_name}" deaktiviert`);
      fetchProducts();
    } catch (e) {
      toast.error('Fehler beim Deaktivieren');
    }
  };

  const handleSubmitNew = async () => {
    if (!newProduct.product_name.trim()) { toast.error('Produktname erforderlich'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/api/products`, {
        ...newProduct,
        location_id: selectedLocation || undefined,
      }, { headers });
      if (res.data.status === 'already_exists') {
        toast.info('Dieses Produkt existiert bereits im Katalog.');
      } else if (res.data.status === 'approved') {
        toast.success('Produkt direkt hinzugefügt.');
      } else {
        toast.success('Produkt eingereicht — wartet auf Superadmin-Freigabe.');
      }
      setShowNewProduct(false);
      setNewProduct({ product_name: '', gtin: '', brand: '', product_group: '', packaging_type: '', supplier_name: '', supplier_article_number: '' });
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Einreichen');
    } finally { setSubmitting(false); }
  };

  const handleCorrectionSubmit = async () => {
    if (!correction.field_in_question || !correction.proposed_value || !correction.reason) {
      toast.error('Bitte alle Felder ausfüllen'); return;
    }
    try {
      await axios.post(`${API}/api/products/${correctionProduct.id}/correction-request`,
        correction, { headers });
      toast.success('Korrektur-Anfrage eingereicht.');
      setCorrectionProduct(null);
      setCorrection({ field_in_question: '', current_value: '', proposed_value: '', reason: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    }
  };

  const clearFilters = () => { setSearch(''); setFilterGroup(''); setFilterBrand(''); setFilterSupplier(''); setPage(1); };
  const hasActiveFilters = search || filterGroup || filterBrand || filterSupplier;

  // ── Phase-4-Migration ──────────────────────────────────────────────────────
  const handleMigrateClick = async () => {
    setMigrateLoading(true);
    setMigratePreview(null);
    try {
      const res = await axios.post(`${API}/api/superadmin/products/migrate-phase4`,
        { dry_run: true }, { headers });
      setMigratePreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Probe-Lauf');
    } finally {
      setMigrateLoading(false);
    }
  };

  const handleMigrateConfirm = async () => {
    setMigrating(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/products/migrate-phase4`,
        { dry_run: false }, { headers });
      setMigrateResult(res.data);
      setMigratePreview(null);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler bei der Migration');
      setMigratePreview(null);
    } finally {
      setMigrating(false);
    }
  };

  // ── Tabak-Cleanup ──────────────────────────────────────────────────────────
  const handleCleanupClick = async () => {
    setCleanupLoading(true);
    setCleanupPreview(null);
    try {
      const res = await axios.post(`${API}/api/superadmin/products/cleanup-tobacco`,
        { dry_run: true }, { headers });
      setCleanupPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Tabak-Scan');
    } finally {
      setCleanupLoading(false);
    }
  };

  const handleCleanupConfirm = async () => {
    setCleaningUp(true);
    try {
      const res = await axios.post(`${API}/api/superadmin/products/cleanup-tobacco`,
        { dry_run: false }, { headers });
      toast.success(res.data.message);
      setCleanupPreview(null);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Tabak-Cleanup');
      setCleanupPreview(null);
    } finally {
      setCleaningUp(false);
    }
  };

  // ── Superadmin: Produkt bearbeiten ─────────────────────────────────────────
  const handleEditOpen = (product) => {
    setEditProduct(product);
    setEditData({
      product_name: product.product_name || '',
      gtin: product.gtin_canonical || '',
      brand: product.brand || '',
      manufacturer: product.manufacturer || '',
      product_group: product.product_group || '',
      packaging_type: product.packaging_type || '',
      unit: product.unit || '',
      supplier_name: product.supplier_name || '',
      supplier_article_number: product.supplier_article_number || '',
      internal_article_number: product.internal_article_number || '',
      pfand_override: product.pfand_override ?? '',
      backshop_only: product.backshop_only || false,
      description: product.description || '',
    });
  };

  const handleEditSave = async () => {
    if (!editProduct) return;
    if (!editData.product_name?.trim()) { toast.error('Produktname darf nicht leer sein'); return; }
    setEditSaving(true);
    try {
      const payload = {
        ...editData,
        pfand_override: editData.pfand_override !== '' ? parseFloat(editData.pfand_override) : null,
      };
      await axios.put(`${API}/api/superadmin/products/${editProduct.id}`, payload, { headers });
      toast.success('Produkt aktualisiert');
      setEditProduct(null);
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setEditSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-5 max-w-full">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Package className="h-6 w-6 text-primary" /> Produkte
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Zentraler Produktkatalog — {total} Produkte
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {locations.length > 1 && (
              <select
                value={selectedLocation}
                onChange={e => setSelectedLocation(e.target.value)}
                className="h-9 rounded-lg border border-border bg-background px-3 text-sm"
                data-testid="location-select"
              >
                {locations.map(l => (
                  <option key={l.id} value={l.id}>{l.name}</option>
                ))}
              </select>
            )}
            <Button size="sm" variant="outline" onClick={fetchProducts} data-testid="refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
            <Button size="sm" onClick={() => setShowNewProduct(true)} data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-1" /> Produkt einreichen
            </Button>
            {isSuperadmin && (
              <Button size="sm" variant="outline"
                onClick={handleMigrateClick}
                disabled={migrateLoading}
                className="gap-1.5 border-dashed"
                data-testid="migrate-phase4-btn">
                {migrateLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <DatabaseZap className="h-4 w-4" />}
                Bestandsprodukte migrieren
              </Button>
            )}
            {isSuperadmin && (
              <Button size="sm" variant="outline"
                onClick={handleCleanupClick}
                disabled={cleanupLoading}
                className="gap-1.5 border-dashed border-orange-400 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950/20"
                data-testid="cleanup-tobacco-btn">
                {cleanupLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <ShieldAlert className="h-4 w-4" />}
                Tabak-Produkte prüfen
              </Button>
            )}
          </div>
        </div>

        {/* Search + Filter */}
        <div className="space-y-2">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Produktname, EAN, Marke, Hersteller suchen..."
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
                data-testid="search-input"
              />
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowFilters(v => !v)}
              className={showFilters ? 'border-primary text-primary' : ''}>
              <Filter className="h-4 w-4 mr-1" />
              Filter {hasActiveFilters && <span className="ml-1 h-2 w-2 rounded-full bg-primary inline-block" />}
            </Button>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="h-4 w-4 mr-1" /> Zurücksetzen
              </Button>
            )}
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 border border-border rounded-xl bg-muted/30">
              <select value={filterGroup} onChange={e => { setFilterGroup(e.target.value); setPage(1); }}
                className="h-9 rounded-lg border border-border bg-background px-3 text-sm">
                <option value="">Alle Warengruppen</option>
                {PRODUCT_GROUPS.map(g => <option key={g} value={g}>{g.replace('_', ' ')}</option>)}
              </select>
              <Input placeholder="Marke filtern..." value={filterBrand}
                onChange={e => { setFilterBrand(e.target.value); setPage(1); }} />
              <Input placeholder="Lieferant filtern..." value={filterSupplier}
                onChange={e => { setFilterSupplier(e.target.value); setPage(1); }} />
            </div>
          )}
        </div>

        {/* Tabelle */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="font-medium">Keine Produkte gefunden</p>
            {hasActiveFilters && <p className="text-sm mt-1">Filter anpassen oder zurücksetzen</p>}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40">
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap">Produkt</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap hidden md:table-cell">EAN/GTIN</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap hidden lg:table-cell">Warengruppe</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap hidden lg:table-cell">Hersteller / Marke</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap hidden xl:table-cell">Lieferant / Art.-Nr.</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap hidden xl:table-cell">Einheit / Gebinde</th>
                  <th className="text-left px-3 py-2.5 font-medium text-muted-foreground whitespace-nowrap hidden xl:table-cell">Pfand</th>
                  <th className="text-right px-3 py-2.5 font-medium text-muted-foreground">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p, i) => (
                  <tr key={p.id}
                    className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}
                    data-testid={`product-row-${p.id}`}>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        {p.image_url && (
                          <img src={p.image_url} alt="" className="h-8 w-8 object-contain rounded border border-border flex-shrink-0"
                            onError={e => { e.target.style.display = 'none'; }} />
                        )}
                        <div>
                          <p className="font-medium leading-tight">{p.product_name}</p>
                          {p.internal_article_number && (
                            <p className="text-xs text-muted-foreground font-mono">JET: {p.internal_article_number}</p>
                          )}
                          {p.backshop_only && (
                            <span className="text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-1.5 py-0.5 rounded">Backshop</span>
                          )}
                          {p.category_needs_review && (
                            <span className="text-xs bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded ml-1">Warengruppe prüfen</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell">
                      <span className="font-mono text-xs text-muted-foreground">{p.gtin_canonical || '–'}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <span className="text-xs">{p.product_group?.replace('_', ' ') || '–'}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <span className="text-xs">{[p.manufacturer, p.brand].filter(Boolean).join(' / ') || '–'}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden xl:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {p.supplier_name || '–'}
                        {p.supplier_article_number && <span className="font-mono ml-1">#{p.supplier_article_number}</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 hidden xl:table-cell">
                      <span className="text-xs">{[p.unit, p.packaging_type].filter(Boolean).join(' / ') || '–'}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden xl:table-cell">
                      <span className="text-xs">
                        {p.pfand_effective != null ? `${p.pfand_effective.toFixed(2)} €` : '–'}
                        {p.pfand_override != null && <span className="text-amber-600 ml-1">*</span>}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {p.is_active_for_customer ? (
                          <Button size="sm" variant="outline"
                            className="text-xs h-7 text-green-700 border-green-300"
                            onClick={() => handleDeactivate(p)}
                            data-testid={`deactivate-btn-${p.id}`}>
                            <CheckCircle2 className="h-3 w-3 mr-1" /> Aktiv
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline"
                            className="text-xs h-7"
                            onClick={() => handleActivate(p)}
                            data-testid={`activate-btn-${p.id}`}>
                            <Plus className="h-3 w-3 mr-1" /> Aktivieren
                          </Button>
                        )}
                        {isSuperadmin && (
                          <Button size="sm" variant="ghost" className="text-xs h-7 text-blue-600"
                            onClick={() => handleEditOpen(p)}
                            data-testid={`edit-btn-${p.id}`}
                            title="Produkt bearbeiten (Superadmin)">
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                        <Button size="sm" variant="ghost" className="text-xs h-7 text-muted-foreground"
                          onClick={() => {
                            setCorrectionProduct(p);
                            setCorrection({ field_in_question: 'product_name', current_value: p.product_name, proposed_value: '', reason: '' });
                          }}
                          data-testid={`correction-btn-${p.id}`}
                          title="Korrektur melden">
                          <AlertCircle className="h-3 w-3" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginierung */}
        {total > LIMIT && (
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>{(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} von {total}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Zurück</Button>
              <Button size="sm" variant="outline" disabled={page * LIMIT >= total} onClick={() => setPage(p => p + 1)}>Weiter</Button>
            </div>
          </div>
        )}

        {/* Dialog: Neues Produkt einreichen */}
        {showNewProduct && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setShowNewProduct(false)}>
            <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold">Produkt einreichen</h3>
              <p className="text-xs text-muted-foreground">Nicht-Superadmin-Einreichungen werden vor Aufnahme in den Katalog geprüft.</p>
              <div className="space-y-3">
                {[
                  ['Produktname *', 'product_name', 'text', 'z.B. Snickers 50g'],
                  ['EAN / GTIN', 'gtin', 'text', 'z.B. 5900951311505'],
                  ['Marke', 'brand', 'text', 'z.B. Mars'],
                  ['Hersteller', 'manufacturer', 'text'],
                  ['Lieferant', 'supplier_name', 'text', 'z.B. Lekkerland'],
                  ['Lieferanten-Artikelnummer', 'supplier_article_number', 'text'],
                ].map(([label, field]) => (
                  <div key={field}>
                    <label className="text-xs font-medium text-muted-foreground">{label}</label>
                    <Input value={newProduct[field] || ''} onChange={e => setNewProduct(p => ({ ...p, [field]: e.target.value }))}
                      placeholder={label.replace(' *', '')} className="mt-0.5" />
                  </div>
                ))}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Warengruppe</label>
                  <select value={newProduct.product_group || ''} onChange={e => setNewProduct(p => ({ ...p, product_group: e.target.value }))}
                    className="w-full h-9 mt-0.5 rounded-lg border border-border bg-background px-3 text-sm">
                    <option value="">Auswählen...</option>
                    {PRODUCT_GROUPS.map(g => <option key={g} value={g}>{g.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowNewProduct(false)}>Abbrechen</Button>
                <Button className="flex-1" disabled={submitting} onClick={handleSubmitNew}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Einreichen
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog: Korrektur-Anfrage */}
        {correctionProduct && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setCorrectionProduct(null)}>
            <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold">Korrektur melden</h3>
              <p className="text-sm text-muted-foreground">{correctionProduct.product_name}</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Welches Feld ist falsch?</label>
                  <select value={correction.field_in_question}
                    onChange={e => {
                      const field = e.target.value;
                      setCorrection(c => ({ ...c, field_in_question: field, current_value: correctionProduct[field] || '' }));
                    }}
                    className="w-full h-9 mt-0.5 rounded-lg border border-border bg-background px-3 text-sm">
                    <option value="">Auswählen...</option>
                    {[['product_name','Produktname'],['gtin_canonical','EAN/GTIN'],['brand','Marke'],['manufacturer','Hersteller'],['product_group','Warengruppe'],['packaging_type','Gebindeart'],['unit','Einheit'],['pfand_effective','Pfand']].map(([v,l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                </div>
                {correction.current_value && (
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Aktueller Wert</label>
                    <Input value={correction.current_value} disabled className="mt-0.5 opacity-60" />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Korrekter Wert</label>
                  <Input value={correction.proposed_value} onChange={e => setCorrection(c => ({ ...c, proposed_value: e.target.value }))}
                    placeholder="Richtiger Wert..." className="mt-0.5" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Begründung *</label>
                  <Input value={correction.reason} onChange={e => setCorrection(c => ({ ...c, reason: e.target.value }))}
                    placeholder="Warum ist die Korrektur nötig?" className="mt-0.5" />
                </div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCorrectionProduct(null)}>Abbrechen</Button>
                <Button className="flex-1" onClick={handleCorrectionSubmit}>Anfrage senden</Button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog: Phase-4-Migration Bestätigung (Probe-Lauf Ergebnis) */}
        {migratePreview && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setMigratePreview(null)}>
            <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4"
              onClick={e => e.stopPropagation()}
              data-testid="migrate-preview-dialog">
              <div className="flex items-center gap-2">
                <DatabaseZap className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-bold">Bestandsprodukte migrieren</h3>
              </div>
              <p className="text-sm text-muted-foreground">Probe-Lauf abgeschlossen. Das würde die Migration ergeben:</p>

              <div className="rounded-xl border border-border divide-y divide-border text-sm">
                {[
                  ['Neue Produkte migriert', migratePreview.stats?.migrated, 'text-green-700 dark:text-green-400', '✓'],
                  ['Bereits vorhanden (übersprungen)', migratePreview.stats?.skipped_already_exists, 'text-muted-foreground', '→'],
                  ['Tabak/Zigaretten ausgeschlossen', migratePreview.stats?.skipped_tabak, 'text-orange-600', '✗'],
                  ['Zeitschriften ausgeschlossen', migratePreview.stats?.skipped_zeitschriften, 'text-orange-600', '✗'],
                ].map(([label, val, cls, icon]) => (
                  <div key={label} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-muted-foreground">{icon} {label}</span>
                    <span className={`font-semibold tabular-nums ${cls}`}>{val ?? '–'}</span>
                  </div>
                ))}
              </div>

              {migratePreview.stats?.migrated === 0 ? (
                <p className="text-sm text-muted-foreground bg-muted/40 rounded-lg px-4 py-3">
                  Alle Produkte sind bereits im Katalog. Keine Migration notwendig.
                </p>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Nach der Migration wären <strong>{migratePreview.total_in_products_master_after}</strong> Produkte im Katalog.
                </p>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1"
                  onClick={() => setMigratePreview(null)}>
                  Abbrechen
                </Button>
                <Button className="flex-1 gap-1.5"
                  disabled={migrating || migratePreview.stats?.migrated === 0}
                  onClick={handleMigrateConfirm}
                  data-testid="migrate-confirm-btn">
                  {migrating ? <Loader2 className="h-4 w-4 animate-spin" /> : <DatabaseZap className="h-4 w-4" />}
                  {migrating ? 'Migriere…' : 'Jetzt migrieren'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Dialog: Migration abgeschlossen */}
        {migrateResult && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setMigrateResult(null)}>
            <div className="bg-background rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4"
              onClick={e => e.stopPropagation()}
              data-testid="migrate-result-dialog">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <h3 className="text-lg font-bold">Migration abgeschlossen</h3>
              </div>
              <div className="rounded-xl border border-border divide-y divide-border text-sm">
                {[
                  ['Neu migriert', migrateResult.stats?.migrated, 'text-green-700 dark:text-green-400'],
                  ['Übersprungen (vorhanden)', migrateResult.stats?.skipped_already_exists, 'text-muted-foreground'],
                  ['Tabak ausgeschlossen', migrateResult.stats?.skipped_tabak, 'text-orange-600'],
                  ['Gesamt im Katalog', migrateResult.total_in_products_master_after, 'text-primary font-bold'],
                ].map(([label, val, cls]) => (
                  <div key={label} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-muted-foreground">{label}</span>
                    <span className={`font-semibold tabular-nums ${cls}`}>{val ?? '–'}</span>
                  </div>
                ))}
              </div>
              <Button className="w-full" onClick={() => setMigrateResult(null)}
                data-testid="migrate-result-close">
                Schließen
              </Button>
            </div>
          </div>
        )}

        {/* Dialog: Tabak-Cleanup Bestätigung */}
        {cleanupPreview && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setCleanupPreview(null)}>
            <div className="bg-background rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4"
              onClick={e => e.stopPropagation()}
              data-testid="cleanup-tobacco-dialog">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-orange-500" />
                <h3 className="text-lg font-bold">Tabak-Produkte prüfen & entfernen</h3>
              </div>

              {cleanupPreview.found_tobacco === 0 ? (
                <p className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-950/20 rounded-lg px-4 py-3">
                  Keine Tabak- oder Shisha-Produkte gefunden. Katalog ist sauber.
                </p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">
                    Der Scan hat <strong className="text-orange-600">{cleanupPreview.found_tobacco} Tabak/Shisha-Produkte</strong> gefunden,
                    die aus dem Katalog entfernt werden sollten:
                  </p>

                  <div className="max-h-60 overflow-y-auto rounded-xl border border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/10">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-orange-100 dark:bg-orange-900/30">
                        <tr>
                          <th className="text-left px-3 py-2 font-medium text-orange-800 dark:text-orange-300">Produktname</th>
                          <th className="text-left px-3 py-2 font-medium text-orange-800 dark:text-orange-300 hidden sm:table-cell">Lekk.-Nr.</th>
                          <th className="text-left px-3 py-2 font-medium text-orange-800 dark:text-orange-300 hidden sm:table-cell">Grund</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cleanupPreview.tobacco_list.map((item, i) => (
                          <tr key={item.id} className={i % 2 === 0 ? 'bg-transparent' : 'bg-orange-50/50'}>
                            <td className="px-3 py-1.5">{item.product_name}</td>
                            <td className="px-3 py-1.5 font-mono hidden sm:table-cell">{item.supplier_article_number || '–'}</td>
                            <td className="px-3 py-1.5 text-orange-600 hidden sm:table-cell">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Nach dem Entfernen bleiben <strong>{cleanupPreview.total_remaining_after}</strong> Produkte im Katalog.
                  </p>
                </>
              )}

              <div className="flex gap-2 pt-1">
                <Button variant="outline" className="flex-1"
                  onClick={() => setCleanupPreview(null)}>
                  Abbrechen
                </Button>
                {cleanupPreview.found_tobacco > 0 && (
                  <Button className="flex-1 gap-1.5 bg-orange-600 hover:bg-orange-700 text-white"
                    disabled={cleaningUp}
                    onClick={handleCleanupConfirm}
                    data-testid="cleanup-confirm-btn">
                    {cleaningUp ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    {cleaningUp ? 'Entferne…' : `${cleanupPreview.found_tobacco} entfernen`}
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Dialog: Produkt bearbeiten (Superadmin) */}
        {editProduct && (
          <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => setEditProduct(null)}>
            <div className="bg-background rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="edit-product-dialog">
              <div className="flex items-center gap-2">
                <Pencil className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-bold">Produkt bearbeiten</h3>
                <span className="text-xs text-muted-foreground ml-auto">Superadmin</span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Produktname */}
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-muted-foreground">Produktname *</label>
                  <Input value={editData.product_name || ''} className="mt-0.5"
                    onChange={e => setEditData(d => ({ ...d, product_name: e.target.value }))}
                    data-testid="edit-product-name" />
                </div>

                {/* EAN/GTIN */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">EAN / GTIN</label>
                  <Input value={editData.gtin || ''} placeholder="z.B. 4005808004025" className="mt-0.5 font-mono text-sm"
                    onChange={e => setEditData(d => ({ ...d, gtin: e.target.value }))}
                    data-testid="edit-gtin" />
                </div>

                {/* Warengruppe */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Warengruppe</label>
                  <select value={editData.product_group || ''} className="w-full h-9 mt-0.5 rounded-lg border border-border bg-background px-3 text-sm"
                    onChange={e => setEditData(d => ({ ...d, product_group: e.target.value }))}
                    data-testid="edit-product-group">
                    <option value="">– keine –</option>
                    {PRODUCT_GROUPS.map(g => <option key={g} value={g}>{g.replace('_', ' ')}</option>)}
                  </select>
                </div>

                {/* Marke */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Marke</label>
                  <Input value={editData.brand || ''} placeholder="z.B. Red Bull" className="mt-0.5"
                    onChange={e => setEditData(d => ({ ...d, brand: e.target.value }))} />
                </div>

                {/* Hersteller */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Hersteller</label>
                  <Input value={editData.manufacturer || ''} placeholder="z.B. Red Bull GmbH" className="mt-0.5"
                    onChange={e => setEditData(d => ({ ...d, manufacturer: e.target.value }))} />
                </div>

                {/* Lieferant */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Lieferant</label>
                  <Input value={editData.supplier_name || ''} placeholder="z.B. Lekkerland" className="mt-0.5"
                    onChange={e => setEditData(d => ({ ...d, supplier_name: e.target.value }))} />
                </div>

                {/* Lief.-Artikelnummer */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Lief.-Artikelnummer</label>
                  <Input value={editData.supplier_article_number || ''} placeholder="z.B. 15663" className="mt-0.5 font-mono text-sm"
                    onChange={e => setEditData(d => ({ ...d, supplier_article_number: e.target.value }))} />
                </div>

                {/* Einheit */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Einheit</label>
                  <Input value={editData.unit || ''} placeholder="z.B. 500ml, 1L, 100g" className="mt-0.5"
                    onChange={e => setEditData(d => ({ ...d, unit: e.target.value }))} />
                </div>

                {/* Gebindeart */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Gebindeart</label>
                  <select value={editData.packaging_type || ''} className="w-full h-9 mt-0.5 rounded-lg border border-border bg-background px-3 text-sm"
                    onChange={e => setEditData(d => ({ ...d, packaging_type: e.target.value }))}>
                    <option value="">– keine –</option>
                    {['dose_einweg','flasche_einweg','flasche_mehrweg','flasche_buegel',
                      'hartplastik_mehrweg','karton','beutel','tüte','becher','sonstige'].map(t => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>

                {/* Pfand */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Pfand-Überschreibung (€)</label>
                  <Input value={editData.pfand_override ?? ''} type="number" step="0.01" min="0"
                    placeholder="z.B. 0.25" className="mt-0.5"
                    onChange={e => setEditData(d => ({ ...d, pfand_override: e.target.value }))} />
                </div>

                {/* JET Intern. Artikelnummer */}
                <div>
                  <label className="text-xs font-medium text-muted-foreground">JET Intern. Artikelnummer</label>
                  <Input value={editData.internal_article_number || ''} placeholder="z.B. 00015663" className="mt-0.5 font-mono text-sm"
                    onChange={e => setEditData(d => ({ ...d, internal_article_number: e.target.value }))} />
                </div>

                {/* Backshop-only */}
                <div className="sm:col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="edit-backshop"
                    checked={!!editData.backshop_only}
                    onChange={e => setEditData(d => ({ ...d, backshop_only: e.target.checked }))}
                    className="h-4 w-4 rounded" />
                  <label htmlFor="edit-backshop" className="text-sm text-muted-foreground cursor-pointer">
                    Nur Backshop-Produkt
                  </label>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1"
                  onClick={() => setEditProduct(null)}>
                  Abbrechen
                </Button>
                <Button className="flex-1 gap-1.5"
                  disabled={editSaving}
                  onClick={handleEditSave}
                  data-testid="edit-save-btn">
                  {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  Speichern
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
